import { type GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { blogPageModuleQuery } from '@data/sanity/queries/page'
import { type SanityBlogPage } from '@data/sanity/queries/types/blog'
import { type SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getBlogLocale, getBlogPage } from '@lib/sanity/blog'
import { defaultModuleQueryParameters } from '@lib/sanity/page'
import { useBlogCanonicalUrl } from '@lib/metadata'
import { type Locale } from '@lib/language'
import {
  type BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { type CustomStaticPropsContext } from '@lib/routes'
import { getVideoObjectSchemas } from '@lib/schema/video'

import Layout from '@components/layout'
import Modules from '@modules/modules'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type ShopPageProps = BasePageProps & {
  blogLocale: Locale
  page: SanityBlogPage
  site: SanitySiteFragment
}

const BlogPage = ({
  draftMode,
  locale,
  blogLocale,
  page,
  site,
}: ShopPageProps) => {
  const canonicalUrl = useBlogCanonicalUrl('blogPage', '', site.blogSettings)

  const videoObjectSchemas = getVideoObjectSchemas(page)
  const schemas = [...videoObjectSchemas]

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      schemas={schemas}
      draftMode={draftMode}
    >
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={blogPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            blogLocale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<ShopPageProps>> {
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const blogLocale = await getBlogLocale(sanityClient, context.locale)
  const blogPage = await getBlogPage(sanityClient, context.locale, blogLocale)

  if (!blogPage.page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      blogLocale: blogLocale as Locale,
      page: getNonNullValues(blogPage.page),
      site: getNonNullValues(blogPage.site),
    },
    revalidate: 60,
  }
}

export default BlogPage
